import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, CircularProgress } from '@material-ui/core';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { useTranslation } from 'react-i18next';
import { Title, PasswordTextField } from '../../elements';
import { PasswordPolicyChecklist } from '../../components';
import {
  notifications,
  extractApiErrorMessage,
  extractApiErrorCode,
  validatePasswordPolicy,
  AuthErrorCode
} from '../../utils';
import { ApiService } from '../../hooks';
import useFeatures, { FeatureType } from '../../hooks/features';
import { brand } from '../../theme/brand';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { hasFeature } = useFeatures();
  const passwordPolicyEnabled = hasFeature(FeatureType.USE_PASSWORD_POLICY);

  const apiService = new ApiService();

  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [confirmError, setConfirmError] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [tokenInvalid, setTokenInvalid] = React.useState(false);

  const policy = validatePasswordPolicy(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = (!passwordPolicyEnabled || policy.isValid) && passwordsMatch && !isBusy;

  const handleSubmit = async () => {
    if (passwordPolicyEnabled && !policy.isValid) {
      notifications.warning(t('f-reset-password-policy-warning'));
      return;
    }

    if (!passwordsMatch) {
      setConfirmError(true);
      notifications.warning(t('f-reset-password-mismatch'));
      return;
    }

    setConfirmError(false);

    if (!token) {
      setTokenInvalid(true);
      return;
    }

    setIsBusy(true);

    await apiService.api.auth
      .authResetPassword({ token, newPassword }, { format: 'json' })
      .then(() => {
        notifications.success(t('f-reset-password-success'));
        navigate('/login');
      })
      .catch((e: unknown) => {
        const code = extractApiErrorCode(e);
        if (code === AuthErrorCode.ResetTokenInvalid) {
          setTokenInvalid(true);
        } else {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-reset-password-error') }));
        }
      })
      .finally(() => setIsBusy(false));
  };

  const renderInvalidLink = () => (
    <div className="p-6">
      <p className="text-sm text-gray-700 mb-4">{t('f-reset-password-invalid-link')}</p>
      <Button variant="contained" color="primary" fullWidth onClick={() => navigate('/forgot-password')}>
        {t('f-reset-password-request-new-link')}
      </Button>
    </div>
  );

  return (
    <div className="flex justify-center py-10 px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gray-50">
          <img src={brand.logoSrc} alt={brand.logoAlt} className="w-8 h-8 rounded-full" />
          <Title>{t('f-reset-password-title')}</Title>
        </div>

        {!token || tokenInvalid ? (
          renderInvalidLink()
        ) : (
          <div className="p-6">
            <PasswordTextField
              variant="outlined"
              margin="dense"
              fullWidth
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              id="resetNewPassword"
              label={t('f-reset-password-new-password')}
              disabled={isBusy}
            />

            {passwordPolicyEnabled && <PasswordPolicyChecklist password={newPassword} />}

            <PasswordTextField
              variant="outlined"
              margin="dense"
              fullWidth
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmError) setConfirmError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              id="resetConfirmPassword"
              label={t('f-reset-password-confirm-password')}
              error={confirmError}
              disabled={isBusy}
            />

            <Button
              id="resetPasswordSubmitBtn"
              startIcon={isBusy ? <CircularProgress size={16} /> : <LockOpenIcon />}
              variant="contained"
              color="primary"
              fullWidth
              disabled={!canSubmit}
              className="!mt-4"
              onClick={handleSubmit}
            >
              {t('f-reset-password-submit')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
